import { type ReactNode, useMemo } from "react";
import { Fields, type TypedWrappedFieldProps } from "redux-form";

import type { OptionBase } from "@braintrust/braintrust-ui-components";
import {
  Box,
  Divider,
  Typography,
  useButtonSelect,
} from "@braintrust/braintrust-ui-components";
import { FlagIcon } from "@braintrust/braintrust-ui-components/Icons";
import { FieldSectionPanelWithImportance } from "@js/apps/jobs/forms/fields";
import { CustomOptionTileButton } from "@js/components/option-tile-buttons";
import { DoubleDatePopoverPickerField } from "@js/forms/fields";
import { shouldDisplayError } from "@js/forms/utils";

import { useGetBoilerplateTermsQuery } from "../../api";
import { openContractTermsPreviewModal } from "../../components/boilerplate-offer-terms-section";
import { OFFER_FIELDS } from "../../constants";
import { useCreateOrEditOfferMeta } from "../../hooks/create-or-edit-offer-meta";

type DateSectionProps = {
  proposed_start_date: TypedWrappedFieldProps<string>;
  proposed_end_date: TypedWrappedFieldProps<string>;
};

export const DateSection = ({
  proposed_start_date,
  proposed_end_date,
}: DateSectionProps) => {
  const displayError = shouldDisplayError([
    proposed_start_date,
    proposed_end_date,
  ]);
  const startDateError = proposed_start_date.meta.error;
  const endDateError = proposed_end_date.meta.error;

  const errorMessage =
    startDateError && endDateError
      ? "Please add start and end date"
      : startDateError || endDateError;

  return (
    <FieldSectionPanelWithImportance
      title="🗓️ Dates"
      fieldImportance="required"
      isError={displayError}
    >
      <Divider color="beige" sx={{ my: 2 }} />
      <Typography component="p" variant="paragraph" sx={{ mb: 2.25 }}>
        Confirm approximate start and end dates.
      </Typography>
      {displayError && (
        <Typography
          component="p"
          variant="paragraph"
          error
          sx={{ mb: 2, mt: -2 }}
        >
          {errorMessage}
        </Typography>
      )}
      <DoubleDatePopoverPickerField
        id="proposed-start-and-end-date"
        label="Add start/end dates"
        labelIcon={<FlagIcon sx={{ fontSize: "16px", marginRight: "8px" }} />}
        startDateField={proposed_start_date}
        endDateField={proposed_end_date}
        hideError
      />
    </FieldSectionPanelWithImportance>
  );
};

const REQUIRE_PAPERWORK_OPTIONS = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

const TALENT_HAS_EQUIPMENT_OPTIONS = [
  { label: "Yes", value: false },
  { label: "No", value: true },
];

export const CONTRACT_TYPE_OPTIONS = [
  { label: "1099", value: ENUMS.ContractTermsType.TERMS_1099 },
  { label: "W2", value: ENUMS.ContractTermsType.TERMS_W2 },
];

const validateContractType =
  (shouldShowContractTypeField: boolean) => (value: unknown) => {
    if (!shouldShowContractTypeField) {
      return;
    }

    if (!value) {
      return "Contract type is required.";
    }
  };

export const DocumentsAndEquipmentSection = () => {
  const offerMeta = useCreateOrEditOfferMeta();
  const shouldShowContractTypeField =
    !!offerMeta?.shouldShowContractTermsTypeField;

  return (
    <Fields
      names={[
        OFFER_FIELDS.talent_has_own_equipment,
        OFFER_FIELDS.require_paperwork,
        OFFER_FIELDS.contract_terms_type,
      ]}
      component={DocumentsAndEquipmentSectionContent}
      shouldShowContractTypeField={shouldShowContractTypeField}
      validate={{
        [OFFER_FIELDS.contract_terms_type]: validateContractType(
          shouldShowContractTypeField,
        ),
      }}
    />
  );
};

type DocumentsAndEquipmentSectionContentProps = {
  talent_has_own_equipment: TypedWrappedFieldProps<boolean>;
  require_paperwork: TypedWrappedFieldProps<boolean>;
  contract_terms_type: TypedWrappedFieldProps<
    EnumType<typeof ENUMS.ContractTermsType>
  >;
  shouldShowContractTypeField: boolean;
};

const DocumentsAndEquipmentSectionContent = ({
  talent_has_own_equipment,
  require_paperwork,
  contract_terms_type,
  shouldShowContractTypeField,
}: DocumentsAndEquipmentSectionContentProps) => {
  const displayError = shouldDisplayError([
    talent_has_own_equipment,
    require_paperwork,
    contract_terms_type,
  ]);
  const offerMeta = useCreateOrEditOfferMeta();

  const errorMessage = displayError
    ? contract_terms_type.meta.error
    : undefined;

  return (
    <FieldSectionPanelWithImportance
      title="📎 Documents and equipment"
      fieldImportance="required"
      isError={displayError}
    >
      {!!errorMessage && (
        <Typography component="p" variant="paragraph" error sx={{ mb: 2 }}>
          {errorMessage}
        </Typography>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <DocumentsAndEquipmentSectionItem
          title={`Do you require additional paperwork for ${offerMeta?.talent_name}?`}
          subtitle="Braintrust will coordinate additional paperwork (NDA, MSA, SOW, etc.)"
          tileButtons={
            <TileButtons
              value={require_paperwork.input.value}
              onChange={require_paperwork.input.onChange}
              options={REQUIRE_PAPERWORK_OPTIONS}
            />
          }
        />
        <Divider color="beige" />
        <DocumentsAndEquipmentSectionItem
          title={`Will you provide any equipment for ${offerMeta?.talent_name}?`}
          subtitle={`If so, Braintrust will gather shipping information from ${offerMeta?.talent_name}`}
          tileButtons={
            <TileButtons
              value={talent_has_own_equipment.input.value}
              onChange={talent_has_own_equipment.input.onChange}
              options={TALENT_HAS_EQUIPMENT_OPTIONS}
            />
          }
        />
        {shouldShowContractTypeField && (
          <OfferContractTypeField contract_terms_type={contract_terms_type} />
        )}
      </Box>
    </FieldSectionPanelWithImportance>
  );
};

const termsLabels = {
  [ENUMS.ContractTermsType.TERMS_1099]: "1099",
  [ENUMS.ContractTermsType.TERMS_W2]: "W2",
} as const;

const OfferContractTypeField = ({
  contract_terms_type,
}: {
  contract_terms_type: TypedWrappedFieldProps<
    EnumType<typeof ENUMS.ContractTermsType>
  >;
}) => {
  const { data } = useGetBoilerplateTermsQuery();
  const contractTermsValue = contract_terms_type.input.value;
  const contractTerms = useMemo(() => {
    return data?.find(
      (terms) => terms.contract_terms_type === contractTermsValue,
    )?.contract_terms;
  }, [contractTermsValue, data]);

  return (
    <>
      <Divider color="beige" />
      <DocumentsAndEquipmentSectionItem
        title="Is this a 1099 Contractor or W2 Contractor offer?"
        subtitle={
          <>
            {!contractTermsValue && (
              <>Select offer type to preview the terms.</>
            )}
            {!!contractTermsValue && !contractTerms && (
              <>Loading contract terms preview…</>
            )}
            {!!contractTerms && (
              <>
                Preview{" "}
                <Typography
                  component="span"
                  role="button"
                  sx={{ textDecoration: "underline", cursor: "pointer" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    openContractTermsPreviewModal(contractTerms);
                  }}
                >
                  {termsLabels[contractTermsValue] ?? ""} Contract terms
                </Typography>
              </>
            )}
          </>
        }
        tileButtons={
          <ContractTermsTypeField
            value={contract_terms_type.input.value}
            onChange={contract_terms_type.input.onChange}
          />
        }
      />
    </>
  );
};

type ContractTermsTypeFieldProps = {
  value: EnumType<typeof ENUMS.ContractTermsType> | null;
  onChange: (value: EnumType<typeof ENUMS.ContractTermsType> | null) => void;
};

export const ContractTermsTypeField = ({
  value,
  onChange,
}: ContractTermsTypeFieldProps) => {
  return (
    <TileButtons
      value={value}
      onChange={onChange}
      options={CONTRACT_TYPE_OPTIONS}
      data-testid="contract-terms-type"
    />
  );
};

type DocumentsAndEquipmentSectionItemProps = {
  title: string;
  subtitle?: ReactNode;
  tileButtons: ReactNode;
};

const DocumentsAndEquipmentSectionItem = ({
  title,
  subtitle,
  tileButtons,
}: DocumentsAndEquipmentSectionItemProps) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        flexDirection: { xs: "column", sm: "row" },
        gap: 1,
        overflowWrap: "anywhere",
        alignItems: { xs: "start", sm: "center" },
      }}
    >
      <Box>
        <Typography component="p" variant="paragraph" size="large">
          {title}
        </Typography>
        {!!subtitle && (
          <Typography
            component="p"
            variant="paragraph"
            size="medium"
            color="tertiary"
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      <Box>{tileButtons}</Box>
    </Box>
  );
};

type TileButtonsProps<TValue> = {
  value: NoInfer<TValue> | null;
  onChange: (value: NoInfer<TValue> | null) => void;
  options: OptionBase<TValue>[];
  ["data-testid"]?: string;
};

export const TileButtons = <TValue,>({
  value,
  onChange,
  options,
  "data-testid": dataTestId,
}: TileButtonsProps<TValue>) => {
  const { isSelected, handleChange, itemA11yProps, containerA11yProps } =
    useButtonSelect({
      value,
      onChange,
    });

  return (
    <Box
      {...containerA11yProps}
      data-testid={dataTestId}
      sx={{ display: "flex", gap: 1 }}
    >
      {options.map((option) => {
        const isOptionSelected = isSelected(option);
        return (
          <CustomOptionTileButton
            key={option.label as string}
            {...itemA11yProps(isOptionSelected)}
            sx={{
              minWidth: "72px !important",
              width: "fit-content !important",
              height: "72px !important",
            }}
            handleChange={handleChange}
            variant="white-violet"
            className="button-shadow"
            option={option}
            isSelected={isOptionSelected}
          />
        );
      })}
    </Box>
  );
};
